import type { Degree, Distance, Margins, SpatialRectangle } from "@pdfking/math"
import {
  AddMargins,
  Center,
  FlipMargins,
  Min,
  RotateMarginsLeft,
  Scale,
  Subtract,
  Total,
} from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageNumber, PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, PlotFunction } from "@pdfking/renderer-api"
import type { GrowContentMeta } from "@pdfking/templates"
import { GrowContentKind } from "@pdfking/templates"

import { EDGE, PALETTE } from "#constants"
import type { ContentLabels, Measure, PageContext, PlacedPiece } from "#internal"
import type { ContentPiece, ContentRequest } from "#public"

import { DrawBackpackGrid } from "../pattern/DrawBackpackGrid"
import { CreateRandom } from "../random/CreateRandom"

export type Box = SpatialRectangle<Distance>

/** A page of pieces as a renderer draws it: the request's pieces, each
 * `placed` where the page shows it — as laid out on the outside, the odd
 * pages; on the inside mirrored along the opening, as a sheet turned over
 * is —, `short` the shortest side of its pages, the page's own without one,
 * and `number` the page a piece shows on the side drawn; the page itself as
 * it is drawn (see PageContext), its pieces' pages drawn as it with their
 * own. */
export type Composition = PageContext & {
  opening_top: boolean
  pieces: ContentPiece[]
  is_front: boolean
  page: Box
  placed: Box[]
  short: Distance
  number: (piece: ContentPiece) => PageNumber | undefined
}

export const CreateComposition = (
  measure: Measure,
  labels: ContentLabels,
  {
    bleed,
    is_cover,
    language,
    one_sided,
    opening_top,
    page_number,
    pieces,
    security,
    seed,
    size,
    total_pages,
    year,
  }: ContentRequest
): Composition => {
  const { height, width } = size
  const is_front = page_number % 2 === 1
  const place = (box: Box): Box => {
    if (is_front) return box
    return opening_top
      ? { ...box, y: Subtract(height, Total(box.y, box.height)) }
      : { ...box, x: Subtract(width, Total(box.x, box.width)) }
  }
  const pages = pieces.filter(piece => piece.enum === GrowContentKind.PAGE)
  return {
    bleed,
    is_cover,
    is_front,
    labels,
    language,
    measure,
    number: piece => (is_front ? piece.page_front : piece.page_rear),
    one_sided,
    opening_top,
    page: { ...size, x: ZERO_DISTANCE, y: ZERO_DISTANCE },
    page_number,
    pieces,
    placed: pieces.map(piece => place(piece.box)),
    // as the side drawn has them, turned over like its pieces
    security: is_front ? security : FlipMargins(security, opening_top ? "y" : "x"),
    seed,
    short: Min(width, height, ...pages.map(piece => Min(piece.box.width, piece.box.height))),
    size,
    total_pages,
    year,
  }
}

type Rotation = GrowContentMeta["rotation"]

/** A turn of `shapes` about the center of `box`, counter-clockwise: a half
 * one unless said. */
export const Turned = (box: Box, shapes: PageShape[], rotation: Rotation = 180): PageShape => ({
  enum: PlotFunction.PLOT_GROUP,
  matrix: GroupMatrix({ origin: Center(box), rotation: rotation as Degree }),
  shapes,
})

/** The box a piece at `box` turned a quarter is drawn in before it is
 * turned: as wide as it is high, about the same center. */
const QuarterBox = (box: Box): Box => {
  const { x, y } = Center(box)
  return {
    x: Subtract(x, Scale(box.height, 0.5)),
    y: Subtract(y, Scale(box.width, 0.5)),
    width: box.height,
    height: box.width,
  }
}

/** How far past a piece at `box` its ground runs: the bleed on every edge of
 * the page it is at, read in the piece's own frame, turned `rotation`. */
export const Reach = (
  { bleed, size: { height, width } }: Pick<PageContext, "bleed" | "size">,
  box: Box,
  rotation: Rotation
): Margins<Distance> => {
  const left = box.x <= EDGE ? bleed.left : ZERO_DISTANCE
  const right = Total(box.x, box.width) >= Subtract(width, EDGE) ? bleed.right : ZERO_DISTANCE
  const bottom = box.y <= EDGE ? bleed.bottom : ZERO_DISTANCE
  const top = Total(box.y, box.height) >= Subtract(height, EDGE) ? bleed.top : ZERO_DISTANCE
  // turning back by `rotation` is turning on by what is left of a full turn
  let reach: Margins<Distance> = { bottom, left, right, top }
  for (let i = 0; i < ((360 - rotation) / 90) % 4; i++) reach = RotateMarginsLeft(reach)
  return reach
}

/** The page's ground, bleed and all: the primary on the outside, a pattern
 * on paper on the inside, a grid of its own filling each piece, so they run
 * on into each other across the folds. */
export const Ground = (c: Composition): PageShape[] =>
  c.is_front
    ? [
        {
          enum: PlotFunction.PLOT_RECT,
          fill_color: PALETTE.primary,
          position: AddMargins(c.page, c.bleed),
        },
      ]
    : c.placed.flatMap((box, i) =>
        DrawBackpackGrid({
          bleed: Reach(c, box, 0),
          box,
          random: CreateRandom(c.seed, "inside", i),
          short: c.short,
        })
      )

/** Every piece drawn by its kind's `draw` where the page shows it (see
 * PlacedPiece); a piece laid turned is drawn upright and turned, a quarter
 * turned one in its box turned with it, unless `draw` turns one upside down
 * itself (`self_turned`), a poster with its row. */
export const ComposePieces = (
  c: Composition,
  draw: Record<GrowContentKind, (p: PlacedPiece) => PageShape[]>,
  self_turned = false
): PageShape[] =>
  c.pieces.flatMap((piece, index) => {
    const box = c.placed[index]!
    const { rotation } = piece
    const turned = rotation === 180
    const shapes = draw[piece.enum]({
      box: rotation % 180 ? QuarterBox(box) : box,
      index,
      number: c.number(piece),
      piece,
      reach: Reach(c, box, rotation),
      // its security its own, turned with it
      security: c.security,
      turned,
    })
    return rotation && !(turned && self_turned) ? [Turned(box, shapes, rotation)] : shapes
  })
